import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { DocumentTypesProvider } from './context/DocumentTypesContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './layouts/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import DocumentsPage from './pages/DocumentsPage';
import DocumentDetailPage from './pages/DocumentDetailPage';
import SyncStatusPage from './pages/SyncStatusPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import SitesPage from './pages/SitesPage';
import SyncSourcesPage from './pages/SyncSourcesPage';
import DocumentTypesPage from './pages/DocumentTypesPage';
import CrmTokenReceiver from './components/CrmTokenReceiver';

// Spinner dùng chung khi đang chờ xác thực
const LoadingSpinner = () => (
  <div style={{
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8fafc'
  }}>
    <div style={{
      width: 32, height: 32,
      border: '3px solid #e2e8f0',
      borderTop: '3px solid #3b82f6',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

/**
 * ProtectedRoute:
 *   - loading=true  → hiện spinner (chờ auth hoặc CrmTokenReceiver exchange token)
 *   - Không có user + không trong iframe → redirect /login
 *   - Không có user + trong iframe → spinner (CrmTokenReceiver đang chạy)
 *   - Có user → render content
 */
// Timeout (ms) to wait for CrmTokenReceiver SSO before falling back to login page
const IFRAME_AUTH_TIMEOUT_MS = 10000;

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const isInIframe = window.parent !== window;
  const [iframeTimeout, setIframeTimeout] = useState(false);

  // If in iframe and still no user after loading completes, start a timeout.
  // After IFRAME_AUTH_TIMEOUT_MS, fall through to <Navigate to="/login"> so the
  // Doc Search login page renders inside Portal instead of an infinite spinner.
  useEffect(() => {
    if (!loading && !user && isInIframe) {
      const timer = setTimeout(() => setIframeTimeout(true), IFRAME_AUTH_TIMEOUT_MS);
      return () => clearTimeout(timer);
    }
  }, [loading, user, isInIframe]);

  if (loading) return <LoadingSpinner />;
  if (!user && isInIframe && !iframeTimeout) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_superuser) return <Navigate to="/" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null; // Đừng flash login nếu đang loading
  if (user) return <Navigate to="/" replace />;
  return children;
};

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

/**
 * AppWithAuth — phải nằm TRONG AuthProvider để dùng useAuth().
 *
 * CrmTokenReceiver được render ở đây (không phải trong AuthProvider)
 * để nó luôn chạy được bất kể trạng thái loading.
 * Vì AuthProvider giờ luôn render children, CrmTokenReceiver sẽ mount ngay lập tức.
 */
function AppWithAuth() {
  const { fetchProfile } = useAuth();

  // Khi CrmTokenReceiver exchange xong → trigger fetchProfile để set user
  const handleTokenReceived = () => {
    if (fetchProfile) fetchProfile();
  };

  return (
    <>
      {/* CrmTokenReceiver chạy ngầm — KHÔNG render UI */}
      <CrmTokenReceiver onTokenReceived={handleTokenReceived} />

      <Routes>
        <Route path="/login" element={
          <PublicRoute><LoginPage /></PublicRoute>
        } />

        <Route path="/" element={
          <ProtectedRoute><Layout /></ProtectedRoute>
        }>
          <Route index element={<DocumentsPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="documents/:id" element={<DocumentDetailPage />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="sync" element={<SyncStatusPage />} />
          <Route path="users" element={
            <AdminRoute><UsersPage /></AdminRoute>
          } />
          <Route path="settings" element={
            <AdminRoute><SettingsPage /></AdminRoute>
          } />
          <Route path="sites" element={
            <AdminRoute><SitesPage /></AdminRoute>
          } />
          <Route path="sync-sources" element={
            <AdminRoute><SyncSourcesPage /></AdminRoute>
          } />
          <Route path="document-types" element={
            <AdminRoute><DocumentTypesPage /></AdminRoute>
          } />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AuthProvider>
          {/* Inside AuthProvider: the catalog fetch needs the auth token */}
          <DocumentTypesProvider>
            <AppWithAuth />
          </DocumentTypesProvider>
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
